import { useState, useEffect, useCallback } from "react";
import { CalendarClock, LayoutGrid, Plus, Rows3 } from "lucide-react";
import { Loader } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import type { StaffUser } from "../../../lib/auth";
import { addLeadActivity, type Lead, type LeadStatus, getLeadTags, getLeads } from "../../../lib/clients";
import { PopHost } from "../../shell/Pop";
import { Btn, MenuField, PageHead, PillTabs, SearchField, Toolbar, ToolbarGap } from "../../ui";
import { LeadsBoard } from "./LeadsBoard";
import { LeadsList } from "./LeadsList";
import { NewLeadModal } from "./NewLeadModal";
import { NextStepDialog } from "../_shared/NextStep";

const ALL = "all";

/** Board or table. Two shapes of the same set, not two sets. */
type View = "board" | "table";

const VIEW_KEY = "wa_crm_leads_view";

/**
 * The chosen shape survives a reload and a trip to a record and back.
 *
 * An operator who works this screen as a list works it as a list all day, and a
 * switch that resets to the board on every navigation is a switch they have to
 * press again every time - which reads as the page forgetting rather than as a
 * default.
 */
function readView(): View {
  if (typeof window === "undefined") return "board";
  return window.localStorage.getItem(VIEW_KEY) === "table" ? "table" : "board";
}

/** The lead screen: the same set of records drawn either as a board or as a list. */
export function LeadsPage({ user: _user }: { user: StaffUser }) {
  const t = useT();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [q, setQ] = useState("");
  const [tag, setTag] = useState<string>(ALL);
  const [tagOptions, setTagOptions] = useState<string[]>([]);
  const [dueOnly, setDueOnly] = useState(false);
  const [planning, setPlanning] = useState<Lead | null>(null);
  const [view, setView] = useState<View>(readView);

  function chooseView(next: View) {
    setView(next);
    window.localStorage.setItem(VIEW_KEY, next);
  }

  // The tag filter goes to the SERVER while the text search stays local, and the
  // asymmetry is deliberate rather than an oversight. The board asks for 200
  // leads and groups them here, so a local filter can only ever narrow those
  // 200 - which is fine for a name somebody is typing from memory, and wrong for
  // a label, because the whole point of filtering by one is to find the records
  // you were not already looking at. Filtering server-side also lets the GIN
  // index on the column do the work.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getLeads({
        pageSize: 200,
        tag: tag === ALL ? undefined : tag,
        nextStep: dueOnly ? "due" : undefined,
      });
      setLeads(res.rows);
    } finally {
      setLoading(false);
    }
  }, [tag, dueOnly]);

  useEffect(() => { load(); }, [load]);

  // Once, not per filter change: the vocabulary is a menu, and reloading it on
  // every selection would make the list of options flicker while the operator
  // is reading it.
  useEffect(() => {
    let alive = true;
    getLeadTags()
      .then((res) => { if (alive) setTagOptions(res.rows); })
      .catch(() => undefined);
    return () => { alive = false; };
  }, []);

  function handleUpdate(id: string, status: LeadStatus) {
    setLeads((prev) => prev.map((l) => l.id === id ? { ...l, status } : l));
  }
  function handleDelete(id: string) {
    setLeads((prev) => prev.filter((l) => l.id !== id));
  }
  function handleConvert(id: string) {
    setLeads((prev) => prev.map((l) => l.id === id ? { ...l, status: "won", convertedClientId: "converted" } : l));
  }

  const filtered = q.trim()
    ? leads.filter((l) => l.name.toLowerCase().includes(q.toLowerCase()) || l.email?.includes(q) || l.regCode?.includes(q))
    : leads;

  return (
    // One open menu at a time across the toolbar and every row - and, before
    // that, ANY open menu at all: a `Pop` with no host above it renders, takes
    // the click and does nothing, which is how this screen's tag filter came to
    // be a button that never opened.
    <PopHost>
      <PageHead
        // The `leads.*` namespace is shared with the scanner's call queue, so
        // the CRM lead list takes its own name from the nav.
        title={t("nav.leads")}
        sub={t("leads.count", { count: leads.length })}
        actions={
          <Btn tone="brand" onClick={() => setShowNew(true)}>
            <Plus className="size-4" /> {t("leads.add")}
          </Btn>
        }
      />

      {/* The filters sit in their own row rather than in the head, which is
          where they used to be. Every head control is taken out of the title's
          width (see `.crm-head-act` in index.css), and four of them left the
          page name in a column too narrow to read. */}
      <Toolbar>
        <SearchField value={q} onChange={setQ} placeholder={t("leads.search")} className="w-52" />
        {/* Absent until there is a label to pick. An empty menu reads as a
            broken control rather than as an empty vocabulary. */}
        {tagOptions.length > 0 ? (
          <MenuField
            value={tag}
            onChange={setTag}
            label={t("leads.tags")}
            options={[
              { value: ALL, label: t("leads.allTags") },
              ...tagOptions.map((tg) => ({ value: tg, label: tg })),
            ]}
          />
        ) : null}
        {/* Both unhappy states at once - nothing planned, and a plan already
            late. The card says which; this narrows to the pile. */}
        <button
          type="button"
          aria-pressed={dueOnly}
          onClick={() => setDueOnly((v) => !v)}
          className={`crm-cb crm-cb-wide${dueOnly ? " is-on" : ""}`}
        >
          <CalendarClock className="size-4" />
          {t("nextStep.filter")}
        </button>
        <ToolbarGap />
        {/* Two glyphs and no wording: a board and a list are SHAPES, and the two
            shapes say themselves faster than the two words do. The wording is
            not thrown away - `PillTabs` hands it to the accessible name and to
            the tooltip. */}
        <PillTabs
          value={view}
          onChange={chooseView}
          iconOnly
          tabs={[
            { value: "board" as View, label: t("leads.view.board"), icon: <LayoutGrid className="size-4" /> },
            { value: "table" as View, label: t("leads.view.table"), icon: <Rows3 className="size-4" /> },
          ]}
        />
      </Toolbar>

      {loading ? (
        <Loader />
      ) : view === "board" ? (
        <LeadsBoard
          leads={filtered}
          t={t}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
          onConvert={handleConvert}
          onPlanStep={setPlanning}
        />
      ) : (
        <LeadsList
          leads={filtered}
          t={t}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
          onConvert={handleConvert}
          onPlanStep={setPlanning}
        />
      )}

      {showNew && (
        <NewLeadModal
          t={t}
          onClose={() => setShowNew(false)}
          onCreate={(lead) => setLeads((prev) => [lead, ...prev])}
        />
      )}

      {planning ? (
        <NextStepDialog
          title={planning.name}
          onClose={() => setPlanning(null)}
          onSave={(body) => addLeadActivity(planning.id, body)}
          onSaved={(activity) => {
            // Patched in rather than reloaded: a reload of the whole board would
            // make the card jump under the cursor, and with the filter on it
            // would vanish before the operator saw the marker clear.
            setLeads((prev) =>
              prev.map((l) => (l.id === planning.id ? { ...l, nextStepAt: activity.dueAt } : l)),
            );
            setPlanning(null);
          }}
        />
      ) : null}
    </PopHost>
  );
}
